import { useEffect, useState } from 'react'
import { api } from './api.js'
import Dialog from './Dialog.jsx'
import Icon from './Icon.jsx'

/**
 * System zurücksetzen.
 *
 * Drei Bremsen, bevor etwas verschwindet: der Umfang muss ausdrücklich
 * angehakt werden, daneben steht jeweils die Zahl der betroffenen Datensätze,
 * und zum Schluss ist das eigene Passwort einzugeben. Letzteres schützt vor
 * allem vor einem unbeaufsichtigten Gerät.
 */
export default function Nullstellung({ beiFertig }) {
  const [offen, setOffen] = useState(false)

  return (
    <>
      <section className="karte karte--gefahr">
        <h2>System zurücksetzen</h2>
        <p className="hinweis hinweis--oben">
          Entfernt erfasste Daten unwiderruflich — gedacht, um nach der Erprobung
          bei null anzufangen. Der Umfang lässt sich wählen.
        </p>
        <button className="knopf knopf--gefahr" type="button" onClick={() => setOffen(true)}>
          <Icon name="muell" /> Zurücksetzen …
        </button>
      </section>

      {offen && <Nullstelldialog beiSchliessen={() => setOffen(false)} beiFertig={beiFertig} />}
    </>
  )
}

const ZAHLZUORDNUNG = {
  zeiten: (b) => `${b.zeiteintraege} Einträge, ${b.monatsblaetter} Blätter`,
  protokoll: (b) => `${b.protokollzeilen} Zeilen`,
  loehne: (b) => `${b.loehne} Sätze`,
  mitarbeiter: (b) => `${b.mitarbeiter} Konten`,
  einstellungen: (b) => `${b.einstellungen} gesetzte Werte`,
}

function Nullstelldialog({ beiSchliessen, beiFertig }) {
  const [stufen, setStufen] = useState(null)
  const [bestand, setBestand] = useState(null)
  const [gewaehlt, setGewaehlt] = useState({})
  const [passwort, setPasswort] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState('')
  const [ergebnis, setErgebnis] = useState(null)

  useEffect(() => {
    api('/nullstellen')
      .then((d) => {
        setStufen(d.stufen)
        setBestand(d.bestand)
      })
      .catch((e) => setFehler(e.message))
  }, [])

  // Konten lassen sich nur räumen, wenn nichts mehr auf sie zeigt — das
  // erzwingt auch der Server, aber hier soll man es vorher sehen.
  const erzwungen = gewaehlt.mitarbeiter
    ? { zeiten: true, protokoll: true, loehne: true }
    : {}
  const wirklich = { ...gewaehlt, ...erzwungen }
  const etwasGewaehlt = Object.values(wirklich).some(Boolean)

  async function ausfuehren() {
    setLaeuft(true)
    setFehler('')
    try {
      const antwort = await api('/nullstellen', {
        methode: 'POST',
        koerper: { passwort, umfang: wirklich },
      })
      setErgebnis(antwort.geloescht)
      setBestand(antwort.bestand)
      beiFertig?.()
    } catch (e) {
      setFehler(e.message)
    } finally {
      setLaeuft(false)
      setPasswort('')
    }
  }

  if (ergebnis) {
    const zeilen = Object.entries(ergebnis).filter(([, zahl]) => zahl > 0)
    return (
      <Dialog titel="Zurückgesetzt" symbol="haken" beiSchliessen={beiSchliessen}>
        <div className="meldung ok">
          <Icon name="haken" groesse={18} />
          <span>Die gewählten Daten wurden gelöscht.</span>
        </div>
        {zeilen.length === 0 ? (
          <p className="hinweis">Es war nichts zu löschen.</p>
        ) : (
          <div className="summenkarte">
            {zeilen.map(([was, zahl]) => (
              <div className="summenzeile" key={was}>
                <span className="gedaempft">{was}</span>
                <strong>{zahl}</strong>
              </div>
            ))}
          </div>
        )}
        <div className="dialog__fuss">
          <button className="knopf knopf--haupt" type="button" onClick={beiSchliessen}>
            Fertig
          </button>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      titel="System zurücksetzen"
      symbol="warnung"
      untertitel="Das Gelöschte lässt sich nicht wiederherstellen."
      beiSchliessen={() => !laeuft && beiSchliessen()}
    >
      {!stufen && !fehler && <p className="hinweis">Einen Moment …</p>}

      {stufen && (
        <>
          {stufen.map((stufe) => {
            const anGezwungen = Boolean(erzwungen[stufe.schluessel])
            return (
              <div className="schalterfeld" key={stufe.schluessel}>
                <button
                  type="button"
                  className="schalter"
                  role="switch"
                  aria-checked={Boolean(wirklich[stufe.schluessel])}
                  aria-labelledby={`stufe-${stufe.schluessel}`}
                  disabled={anGezwungen}
                  onClick={() =>
                    setGewaehlt((b) => ({ ...b, [stufe.schluessel]: !b[stufe.schluessel] }))
                  }
                />
                <div>
                  <span className="schalterfeld__text" id={`stufe-${stufe.schluessel}`}>
                    {stufe.titel}
                    {bestand && (
                      <span className="abzeichen abzeichen--offen" style={{ marginLeft: '.5rem' }}>
                        {ZAHLZUORDNUNG[stufe.schluessel]?.(bestand)}
                      </span>
                    )}
                  </span>
                  <p className="hinweis">
                    {stufe.hilfe}
                    {anGezwungen && ' Wird beim Löschen der Konten zwingend mit entfernt.'}
                  </p>
                </div>
              </div>
            )
          })}

          <div className="feld" style={{ marginTop: '1.25rem' }}>
            <label htmlFor="nullpasswort">Eigenes Passwort zur Bestätigung</label>
            <input
              id="nullpasswort"
              type="password"
              autoComplete="current-password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
            />
          </div>
        </>
      )}

      {fehler && (
        <div className="meldung fehler" role="alert">
          <Icon name="warnung" groesse={18} />
          <span>{fehler}</span>
        </div>
      )}

      <div className="dialog__fuss">
        <button className="knopf knopf--leise" type="button" onClick={beiSchliessen} disabled={laeuft}>
          Abbrechen
        </button>
        <button
          className="knopf knopf--gefahr"
          type="button"
          onClick={ausfuehren}
          disabled={laeuft || !etwasGewaehlt || passwort.length === 0}
        >
          <Icon name="muell" /> {laeuft ? 'Wird gelöscht …' : 'Unwiderruflich löschen'}
        </button>
      </div>
    </Dialog>
  )
}
